import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import CtaButton from '../ctaButton/ctaButton';
import PlayIcon from './PlayIcon';
import styles from './heroAbout.module.scss';

export default function HeroAbout({
  eyebrow = null,
  headline = null,
  description = null,
  videoBackgroundUrl = null,
  videoOverlayUrl = null,
  cta = null,
}) {
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  const openOverlay = useCallback(
    (e) => {
      // CTA has no link as it never navigates, just opens the video overlay
      e?.preventDefault?.();
      if (videoOverlayUrl) setIsOverlayOpen(true);
    },
    [videoOverlayUrl]
  );

  const closeOverlay = useCallback(() => setIsOverlayOpen(false), []);

  useEffect(() => {
    if (!isOverlayOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeOverlay();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOverlayOpen, closeOverlay]);

  return (
    <section className={styles.heroAbout}>
      <div className={styles.topInner}>
        {eyebrow && <p className={styles.eyebrow}>{parse(eyebrow)}</p>}
        {headline && (
          <h1 className={styles.headline}>
            <span className={styles.gradientText}>{parse(headline)}</span>
          </h1>
        )}
      </div>

      <div className={styles.gradientZone}>
        <div className={styles.bottomInner}>
          {videoBackgroundUrl && (
            <div className={styles.videoCard}>
              <div className={styles.videoBackground}>
                <iframe
                  className={styles.videoIframe}
                  src={videoBackgroundUrl}
                  title={cta?.label || 'Background video'}
                  frameBorder="0"
                  allow="autoplay; fullscreen"
                  tabIndex={-1}
                  aria-hidden="true"
                />
              </div>
              {cta?.label && videoOverlayUrl && (
                <div className={styles.ctaWrapper}>
                  <CtaButton
                    className={styles.playButton}
                    variant={cta.variant || 'secondary white'}
                    onClick={openOverlay}
                    data-testid="heroAboutPlay"
                  >
                    <PlayIcon className={styles.playIcon} />
                    <span>{cta.label}</span>
                  </CtaButton>
                </div>
              )}
            </div>
          )}

          {description && (
            <div className={styles.description}>{parse(description)}</div>
          )}
        </div>
      </div>

      {isOverlayOpen && (
        <div
          className={styles.overlay}
          role="dialog"
          aria-modal="true"
          aria-label={cta?.label || 'Video'}
        >
          <button
            type="button"
            className={styles.overlayBackdrop}
            aria-label="Close video"
            onClick={closeOverlay}
          />
          <div className={styles.overlayPopup}>
            <button
              type="button"
              className={styles.overlayClose}
              aria-label="Close video"
              data-testid="heroAboutClose"
              onClick={closeOverlay}
            >
              &times;
            </button>
            <iframe
              className={styles.overlayIframe}
              src={videoOverlayUrl}
              title={cta?.label || 'Video'}
              frameBorder="0"
              allow="autoplay; fullscreen; encrypted-media"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </section>
  );
}

HeroAbout.propTypes = {
  eyebrow: PropTypes.string,
  headline: PropTypes.string,
  description: PropTypes.string,
  videoBackgroundUrl: PropTypes.string,
  videoOverlayUrl: PropTypes.string,
  cta: PropTypes.shape({
    label: PropTypes.string,
    variant: PropTypes.string,
    link: PropTypes.string,
  }),
};
